/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { FC } from "react";
import { Routes, Route, BrowserRouter } from "react-router-dom";
import { PrivateRoutes } from "./PrivateRoutes";
import { ErrorsPage } from "../modules/errors/ErrorsPage";
import { Logout, AuthPage, IAuthState } from "../modules/auth";
import { App } from "../App";
import { RootState } from "setup/redux/RootReducer";
import { shallowEqual, useSelector } from "react-redux";
import TermsAndConditionsPage from "app/modules/auth/termsandconditions/TermsAndConditions";
import TwoFactorAuthSetupPage from "app/modules/auth/twofactorauthentication/TwoFactorAuthSetupPage";
import { TwoFactorAuthenticationTypes } from "global/enums";
import AccountDisabledPage from "app/modules/auth/AccountDisabledPage";
import { CPLAppRoute } from "cpl-react-common/components/Navigation/CPLAppRoute";
import ResetPasswordPage from "app/modules/auth/resetPassword/ResetPassword";
import { PaymentConfirmationStep } from "app/modules/payments/components/steps/PaymentConfirmationStep";
import { PaymentFailedStep } from "app/modules/payments/components/steps/PaymentFailedStep";
import ExternalAccessRoutes from "./ExternalAccessRoutes";
import ExternalAccessLinkHandler from "app/modules/auth/ExternalAccessLinkHandler";
import { selectDownForMaintenance } from "app/system/maintenance/MaintenanceSlice";
import MaintenancePage from "app/system/maintenance/MaintenancePage";
import ExternalAccessLayout from "app/modules/auth/ExternalAccessLayout";
import AccessibilityPage from "app/system/accessibility/AccessibilityPage";
import LoginLinkHandler from "app/modules/auth/LoginLinkHandler";
import { AuthLayout } from "app/modules/auth/AuthLayout";
import { ImpersonationPage } from "app/modules/auth/impersonation/ImpersonationPage";

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env;

// To remove the v7 warning in the console
const routerFuture = { v7_startTransition: false, v7_relativeSplatPath: false };

const AppRoutes: FC = () => {
  const authState = useSelector<RootState, IAuthState>(({ auth }) => auth, shallowEqual);
  const downForMaintenance = useSelector(selectDownForMaintenance);

  const getAppRoutes = () => {
    const authorisedUser = authState.user;

    if (downForMaintenance) {
      return CPLAppRoute({ inputPath: "maintenance", element: <MaintenancePage /> });
    }

    if (authState.externalAccess)
      return CPLAppRoute({
        inputPath: "",
        element: <ExternalAccessRoutes allowedRoute={authState.externalAccess.accessPath} />,
      });

    if (!authorisedUser) {
      return CPLAppRoute({ inputPath: "auth", element: <AuthPage /> });
    }

    if (authorisedUser.accountDisabled)
      return CPLAppRoute({ inputPath: "accountdisabled", element: <AccountDisabledPage /> });

    if (authorisedUser.twoFactorAuthTypeID === TwoFactorAuthenticationTypes.None)
      return CPLAppRoute({ inputPath: "twofactorauthentication", element: <TwoFactorAuthSetupPage /> });

    if (!authorisedUser.termsAndConditionsAcceptanceDate)
      return CPLAppRoute({ inputPath: "termsandconditions", element: <TermsAndConditionsPage /> });

    if (authorisedUser.passwordChangeRequired) {
      return CPLAppRoute({ inputPath: "resetpassword", element: <ResetPasswordPage /> });
    }

    return CPLAppRoute({ inputPath: "", outputPath: "dashboard", isIndex: true, element: <PrivateRoutes /> });
  };

  return (
    <BrowserRouter basename={PUBLIC_URL} future={routerFuture}>
      <Routes>
        <Route element={<App />}>
          <Route path="error/*" element={<ErrorsPage />} />
          <Route path="logout" element={<Logout />} />
          <Route path="accessibility" element={<AccessibilityPage />} />
          <Route path="loginLink/:emailAddress/:password" element={<LoginLinkHandler />} />
          <Route element={<AuthLayout />}>
            <Route path="auth/impersonate" element={<ImpersonationPage />} />
          </Route>
          {getAppRoutes()}
        </Route>
        <Route element={<ExternalAccessLayout />}>
          <Route path="blinksuccess/:paymentID" element={<PaymentConfirmationStep />} />
          <Route path="blinkerror" element={<PaymentFailedStep />} />
          <Route path="externalAccess/:token" element={<ExternalAccessLinkHandler />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export { AppRoutes };
